import { FC, useState, useCallback, useMemo, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { trackInternalEvent } from '@rio/tracking'
import useDebouncedEffect from 'use-debounced-effect'
import ColorPicker from 'builder/components/ColorPicker'
import { useI18n } from 'builder/hooks/useI18n'
import {
  actions as coverLetterAction,
  selectors as coverLetterSelectors,
} from 'builder/modules/coverLetterEditor'
import { DocumentTypes } from 'builder/modules/constants'
import { selectors, actions } from 'builder/modules/resumeEditor'
import { useUpdateTemplateSettings } from '../../hooks/useUpdateTemplateSettings'
import { useTemplateCustomization } from '../../hooks/useTemplateCustomization'
import { ColorPickerPanelContainer, Title, PanelActions, PanelAction } from './styles'

interface ColorPickerPanelProps {
  type: DocumentTypes
  handleClose: () => void
}

export const ColorPickerPanel: FC<ColorPickerPanelProps> = ({ type, handleClose }) => {
  const { i18n } = useI18n()
  const dispatch = useDispatch()
  const [defaultColor, setDefaultColor] = useState<string>()
  const [localColor, setLocalColor] = useState<string>()
  const resume = useSelector(selectors.resume)
  const coverLetter = useSelector(coverLetterSelectors.coverLetter)
  const currentDocument = type === DocumentTypes.resume ? resume : coverLetter
  const { updateTemplateSettings } = useUpdateTemplateSettings()

  const {
    templateName,
    templatesSettings,
    supportsNewCustomization,
    supportsCustomColor,
    currentTemplate,
    resumeSpecificTemplateSettings,
  } = useTemplateCustomization()

  const handleSave = useCallback(
    (color: string) => {
      if (type === DocumentTypes.resume) {
        if (
          color &&
          templatesSettings &&
          supportsNewCustomization &&
          templateName &&
          supportsCustomColor
        ) {
          updateTemplateSettings({
            templatesSettings,
            templateName,
            settingsName: 'color',
            value: color,
          })
        } else {
          dispatch(
            actions.updateSimpleField({
              name: 'color',
              value: color,
              debounce: false,
            }),
          )
        }
        trackInternalEvent('change_resume_color', { template: templateName })
      } else {
        dispatch(
          coverLetterAction.updateSimpleField({
            name: 'color',
            value: color,
            debounce: false,
          }),
        )
      }
    },
    [
      type,
      templatesSettings,
      supportsNewCustomization,
      templateName,
      supportsCustomColor,
      updateTemplateSettings,
      dispatch,
    ],
  )

  const colorSettings = useMemo(() => {
    const templateSettings = currentTemplate?.settings
    const templateColor = templateSettings?.color
    const supportsCustomColor = currentTemplate?.supportsCustomColor

    const getSelectedColor = () => {
      if (currentDocument) {
        if (currentDocument.type === DocumentTypes.resume && supportsNewCustomization) {
          return resumeSpecificTemplateSettings?.color || templateColor?.default
        }
        return currentDocument.color || templateColor?.default
      }
      return templateColor?.default || null
    }

    const selected = getSelectedColor()
    const options = templateColor?.values || []

    return { selected, options, supportsCustomColor }
  }, [
    currentTemplate?.settings,
    currentTemplate?.supportsCustomColor,
    currentDocument,
    supportsNewCustomization,
    resumeSpecificTemplateSettings?.color,
  ])

  useDebouncedEffect(
    () => {
      if (localColor && localColor !== defaultColor) {
        handleSave(localColor)
      }
    },
    350,
    [localColor],
  )

  useEffect(() => {
    if (colorSettings.selected && localColor === defaultColor) {
      setLocalColor(colorSettings.selected)
      setDefaultColor(colorSettings.selected)
    }
  }, [colorSettings.selected, defaultColor, localColor])

  const handleCancel = useCallback(() => {
    if (defaultColor) {
      handleSave(defaultColor)
      handleClose()
    }
  }, [defaultColor, handleClose, handleSave])

  const handleSaveAndClose = useCallback(() => {
    if (localColor) {
      handleSave(localColor)
      handleClose()
    }
  }, [localColor, handleSave, handleClose])

  return (
    <ColorPickerPanelContainer>
      <Title>{i18n.t('builder.resume_editor.color_select')}</Title>
      <ColorPicker hex={localColor} onChange={setLocalColor} />
      <PanelActions>
        <PanelAction onClick={handleCancel}>{i18n.t('builder.resume_editor.cancel')}</PanelAction>
        <PanelAction onClick={handleSaveAndClose}>
          {i18n.t('builder.resume_editor.save')}
        </PanelAction>
      </PanelActions>
    </ColorPickerPanelContainer>
  )
}
